import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import CloseIcon from "@mui/icons-material/Close";
import {
	applySupersedesToDismissed,
	filterAndSortNotices,
	isDismissedPermanently,
	loadNoticesDocument,
	readDismissedMap,
	renderMessageToSafeHtml,
	resolveNoticeAppearance,
	writeDismissedMap,
} from "@ilocalbox/system-notices-client";
import {
	LOG_PREFIX,
	STORAGE_DISMISSED_KEY,
	DEFAULT_POLL_MS,
	PRODUCT,
	NOTICE_CHANNELS,
} from "./constants.js";
import { getNoticeEnvironment } from "./environment.js";
import {
	getPortalAccountId,
	getPortalUserRoles,
	isPortalAuthenticated,
} from "./authContext.js";
import "./system-notices-layout.css";

const GROUP_BAR_ID = "ilocalbox-portal-group-app-bar";

function NoticeGlyph({ severity, stroke }) {
	if (severity === "info") {
		return (
			<svg
				width="20"
				height="20"
				fill="none"
				viewBox="0 0 24 24"
				stroke={stroke}
				aria-hidden
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
				/>
			</svg>
		);
	}
	return (
		<svg
			width="20"
			height="20"
			fill="none"
			viewBox="0 0 24 24"
			stroke={stroke}
			aria-hidden
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
			/>
		</svg>
	);
}

function clearNoticesLayoutShift() {
	const root = document.documentElement;
	root.style.removeProperty("--ilocalbox-system-notices-height");
	root.style.removeProperty("--ilocalbox-portal-group-bar-height");
	root.removeAttribute("data-ilocalbox-system-notices-active");
}

function measureGroupBarHeight() {
	const el = document.getElementById(GROUP_BAR_ID);
	return el ? el.getBoundingClientRect().height : 0;
}

function syncNoticesLayoutShift(stackRef) {
	const root = document.documentElement;
	const h = stackRef.current
		? stackRef.current.getBoundingClientRect().height
		: 0;
	if (h > 0) {
		root.style.setProperty("--ilocalbox-system-notices-height", `${h}px`);
		root.style.setProperty(
			"--ilocalbox-portal-group-bar-height",
			`${measureGroupBarHeight()}px`,
		);
		root.setAttribute("data-ilocalbox-system-notices-active", "");
	} else {
		clearNoticesLayoutShift();
	}
}

function useNoticesLayoutSync(visible, stackRef) {
	useEffect(() => {
		if (!visible.length) {
			clearNoticesLayoutShift();
			return undefined;
		}

		const sync = () => syncNoticesLayoutShift(stackRef);
		sync();

		const observers = [];
		const RO = window.ResizeObserver;
		const observe = (el) => {
			if (!el || !RO) return;
			const ro = new RO(sync);
			ro.observe(el);
			observers.push(ro);
		};

		observe(stackRef.current);

		let groupAttached = false;
		const attachGroupBar = () => {
			if (groupAttached) return true;
			const groupBar = document.getElementById(GROUP_BAR_ID);
			if (!groupBar) return false;
			observe(groupBar);
			groupAttached = true;
			return true;
		};

		let groupPoll;
		if (!attachGroupBar()) {
			groupPoll = window.setInterval(() => {
				sync();
				if (attachGroupBar()) window.clearInterval(groupPoll);
			}, 250);
			window.setTimeout(() => window.clearInterval(groupPoll), 8000);
		}

		window.addEventListener("resize", sync);
		return () => {
			if (groupPoll) window.clearInterval(groupPoll);
			observers.forEach((ro) => ro.disconnect());
			window.removeEventListener("resize", sync);
			clearNoticesLayoutShift();
		};
	}, [visible, stackRef]);
}

/**
 * Full-width top strips. Fixed at viewport top; RxConnect chrome
 * shifts down only while notices are visible.
 */
export default function SystemNoticeBanner() {
	const url = process.env.REACT_APP_SYSTEM_NOTICES_URL;
	const pollMs =
		Number(process.env.REACT_APP_SYSTEM_NOTICES_POLL_MS) || DEFAULT_POLL_MS;
	const region = process.env.REACT_APP_SYSTEM_NOTICES_REGION || "";

	const [visible, setVisible] = useState([]);
	const stackRef = useRef(null);
	const timerRef = useRef(null);
	const mountedRef = useRef(true);
	const authKeyRef = useRef("");

	const pollOnce = useCallback(async () => {
		const env = getNoticeEnvironment();
		const isAuth = isPortalAuthenticated();
		const roles = getPortalUserRoles();
		const ctx = {
			product: PRODUCT,
			environment: env,
			region,
			roles,
			isAuthenticated: isAuth,
			accountId: getPortalAccountId(),
			nowMs: Date.now(),
			renderChannel: NOTICE_CHANNELS.PORTAL_BANNER,
		};

		const { doc, source, error } = await loadNoticesDocument({
			configuredUrl: url,
			logPrefix: LOG_PREFIX,
		});
		if (!mountedRef.current) return;

		if (error) {
			console.warn(LOG_PREFIX, "poll_cycle", { source, error });
		} else {
			console.info(LOG_PREFIX, "poll_cycle", { source });
		}

		if (!doc || !Array.isArray(doc.notices)) {
			setVisible([]);
			return;
		}

		let dismissed = readDismissedMap(STORAGE_DISMISSED_KEY);
		const matched = filterAndSortNotices(doc.notices, ctx);
		const pruned = applySupersedesToDismissed(matched, dismissed);
		if (JSON.stringify(pruned) !== JSON.stringify(dismissed)) {
			writeDismissedMap(pruned, STORAGE_DISMISSED_KEY);
			dismissed = pruned;
		}

		const shown = matched
			.filter((n) => {
				if (!n.dismissible) return true;
				return !isDismissedPermanently(n.id, dismissed);
			})
			.slice(0, 3);

		setVisible(shown);
	}, [url, region]);

	useEffect(() => {
		mountedRef.current = true;
		void pollOnce();
		timerRef.current = setInterval(() => void pollOnce(), pollMs);
		const onFocus = () => void pollOnce();
		window.addEventListener("focus", onFocus);
		return () => {
			mountedRef.current = false;
			if (timerRef.current) clearInterval(timerRef.current);
			window.removeEventListener("focus", onFocus);
		};
	}, [pollOnce, pollMs]);

	useEffect(() => {
		const tick = () => {
			const key = `${sessionStorage.getItem("user")}|${sessionStorage.getItem("parentAccountId")}`;
			if (key !== authKeyRef.current) {
				authKeyRef.current = key;
				void pollOnce();
			}
		};
		authKeyRef.current = `${sessionStorage.getItem("user")}|${sessionStorage.getItem("parentAccountId")}`;
		const id = setInterval(tick, 2000);
		return () => clearInterval(id);
	}, [pollOnce]);

	useNoticesLayoutSync(visible, stackRef);

	const onDismiss = useCallback(
		(id) => {
			const map = readDismissedMap(STORAGE_DISMISSED_KEY);
			map[id] = true;
			writeDismissedMap(map, STORAGE_DISMISSED_KEY);
			void pollOnce();
		},
		[pollOnce],
	);

	const bar = useMemo(() => {
		if (!visible.length) return null;
		return (
			<Box
				ref={stackRef}
				role="region"
				aria-label="System notices"
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					zIndex: 12000,
					display: "flex",
					flexDirection: "column",
					boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
				}}
			>
				{visible.map((n) => {
				const theme = resolveNoticeAppearance(n);
					return (
					<Box
						key={n.id}
						sx={{
							position: "relative",
							display: "flex",
							alignItems: "center",
							gap: { xs: 1.5, sm: 2 },
							px: { xs: 1.5, sm: 2.5 },
							py: 1.5,
							bgcolor: theme.bg,
							color: theme.text,
							borderBottom: `1px solid ${theme.border}`,
							fontSize: "0.875rem",
							lineHeight: 1.45,
						}}
					>
						<Box
							aria-hidden
							sx={{
								position: "absolute",
								left: 0,
								top: 0,
								bottom: 0,
								width: 4,
								bgcolor: theme.accent,
							}}
						/>
						<Box
							sx={{
								ml: 0.5,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 36,
								height: 36,
								borderRadius: "50%",
								bgcolor: theme.iconBg,
								border: `1px solid ${theme.accent}40`,
								flexShrink: 0,
							}}
						>
							<NoticeGlyph
								severity={n.severity}
								stroke={theme.iconStroke}
							/>
						</Box>
						<Box
							sx={{
								flex: 1,
								minWidth: 0,
								display: "flex",
								flexWrap: "wrap",
								alignItems: "baseline",
								columnGap: 0.75,
								rowGap: 0.25,
							}}
						>
							<Box component="span" sx={{ fontWeight: 700 }}>
								{n.title}
							</Box>
							<Box
								component="span"
								sx={{ color: theme.muted }}
								aria-hidden
							>
								·
							</Box>
							<Box
								component="span"
								sx={{
									"& a": {
										color: theme.link,
										fontWeight: 600,
										textDecoration: "underline",
									},
									"& p": { display: "inline", m: 0 },
									"& strong": { fontWeight: 600 },
									"& u": { textDecoration: "underline" },
								}}
								dangerouslySetInnerHTML={{
									__html: renderMessageToSafeHtml(
										n.message,
										n.messageFormat,
									),
								}}
							/>
							{n.detailsUrl ? (
								<React.Fragment>
									<Box
										component="span"
										sx={{ color: theme.muted }}
										aria-hidden
									>
										·
									</Box>
									<Link
										href={n.detailsUrl}
										target="_blank"
										rel="noopener noreferrer"
										sx={{
											color: theme.link,
											fontWeight: 600,
											textDecoration: "none",
											"&:hover": { textDecoration: "underline" },
										}}
									>
										View status →
									</Link>
								</React.Fragment>
							) : null}
						</Box>
						{n.dismissible ? (
								<IconButton
									size="small"
									aria-label="Dismiss notice"
									onClick={() => onDismiss(n.id)}
									sx={{
										flexShrink: 0,
										width: 32,
										height: 32,
										borderRadius: 1,
										border: "1px solid rgba(148, 163, 184, 0.5)",
										bgcolor: "rgba(255, 255, 255, 0.7)",
										color: theme.muted,
										"&:hover": {
											bgcolor: "#fff",
											color: theme.text,
										},
									}}
								>
									<CloseIcon sx={{ fontSize: 16 }} />
								</IconButton>
							) : null}
						</Box>
					);
				})}
			</Box>
		);
	}, [visible, onDismiss]);

	return bar;
}



// WEBPACK FOOTER //
// ./src/system-notices/SystemNoticeBanner.jsx